import { useRef } from "react";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import TextField from "../../../components/Form/TextField";
import Select from "../../../components/Form/Select";
import ColorField from "../../../components/Form/ColorField";
import Button from "../../../components/Buttons/Button";
import Checkbox from "../../../components/Form/Checkbox";
import FontFamilySelect from "./FontFamilySelect";
import { useTranslationApp } from "../../../lib/i18next";
import {
  TEMPLATES_ALIGN,
  TEMPLATES_ALIGN_CENTER,
  TEMPLATES_ALIGN_LEFT,
  TEMPLATES_ALIGN_RIGHT,
  TEMPLATES_ORIENTATION,
  TEMPLATES_ORIENTATION_HORIZONTAL,
  TEMPLATES_ORIENTATION_VERTICAL,
  TEMPLATES_ASSET_REF,
  TEMPLATES_CHANGE_ASSET,
  TEMPLATES_CLIP,
  TEMPLATES_EXPRESSION,
  TEMPLATES_FIXED_VALUE,
  TEMPLATES_FONT_COLOR,
  TEMPLATES_FRAME_H,
  TEMPLATES_FRAME_W,
  TEMPLATES_LAYER_NAME,
  TEMPLATES_MAX_LINES,
  TEMPLATES_MIN_LINES,
  TEMPLATES_NO_SELECTION,
  TEMPLATES_POS_H,
  TEMPLATES_POS_W,
  TEMPLATES_POS_X,
  TEMPLATES_POS_Y,
  TEMPLATES_POSITION,
  TEMPLATES_PROMPT,
  TEMPLATES_IS_BACKGROUND,
  TEMPLATES_SELECT_ASSET,
  TEMPLATES_SIZE,
  TEMPLATES_PROPERTIES,
  TEMPLATES_SOURCE_ASSET,
  TEMPLATES_SOURCE_COMPUTED,
  TEMPLATES_SOURCE_DATA,
  TEMPLATES_SOURCE_FIXED,
  TEMPLATES_SOURCE_GENERATED,
  TEMPLATES_TYPE_GROUP,
  TEMPLATES_TYPE_IMAGE,
  TEMPLATES_TYPE_SHAPE,
  TEMPLATES_TYPE_TEXT,
  TEMPLATES_VARIABLE,
  TEMPLATES_ZONE_SOURCE,
  TEMPLATES_ZONE_TYPE,
  YES,
  NO,
} from "../../../i18n/keysTranslations";

const optionalRules = { required: false };

const FieldGroup = ({ title, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, color: "text.secondary", fontSize: "13px" }}
    >
      {title}
    </Typography>
    <Box sx={{ display: "flex", gap: 1 }}>{children}</Box>
  </Box>
);

const LayerPropertiesPanel = ({
  control,
  errors,
  disabled,
  selectedNode,
  isRootLayer = false,
  assetUrl = "",
  isUploadingAsset = false,
  onChangeField,
  onUploadAsset,
}) => {
  const { t } = useTranslationApp();
  const assetFileInputRef = useRef(null);
  const type = selectedNode?.type;
  const sourceKind = selectedNode?.source?.kind;
  const isCanvasBackground = Boolean(selectedNode?.isBackground);
  const showBackgroundCheckbox =
    isRootLayer && type === "image" && sourceKind === "generated";
  const geometryDisabled = disabled || isCanvasBackground;

  const sourceOptions =
    type === "text"
      ? [
          { value: "fixed", label: t(TEMPLATES_SOURCE_FIXED) },
          { value: "data", label: t(TEMPLATES_SOURCE_DATA) },
          { value: "generated", label: t(TEMPLATES_SOURCE_GENERATED) },
          ...(sourceKind === "computed"
            ? [{ value: "computed", label: t(TEMPLATES_SOURCE_COMPUTED) }]
            : []),
        ]
      : type === "image"
        ? [
            { value: "asset", label: t(TEMPLATES_SOURCE_ASSET) },
            { value: "data", label: t(TEMPLATES_SOURCE_DATA) },
            { value: "generated", label: t(TEMPLATES_SOURCE_GENERATED) },
          ]
        : [];

  const typeOptions = [
    { value: "text", label: t(TEMPLATES_TYPE_TEXT) },
    { value: "image", label: t(TEMPLATES_TYPE_IMAGE) },
    { value: "group", label: t(TEMPLATES_TYPE_GROUP) },
    ...(type === "shape"
      ? [{ value: "shape", label: t(TEMPLATES_TYPE_SHAPE) }]
      : []),
  ];

  if (!selectedNode) {
    return (
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #E4E4E7",
          borderRadius: "14px",
          p: 2,
          minHeight: 360,
        }}
      >
        <Typography sx={{ fontWeight: 500, mb: 1 }}>
          {t(TEMPLATES_PROPERTIES)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(TEMPLATES_NO_SELECTION)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E4E4E7",
        borderRadius: "14px",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minHeight: 360,
      }}
    >
      <Typography sx={{ fontWeight: 500 }}>{t(TEMPLATES_PROPERTIES)}</Typography>
      <TextField
        control={control}
        errors={errors}
        name="layerName"
        label={t(TEMPLATES_LAYER_NAME)}
        variant="filled"
        fullWidth
        disabled={disabled}
        rules={optionalRules}
        actionsChange={(value) => onChangeField({ key: "name", value })}
      />
      <Select
        control={control}
        errors={errors}
        name="type"
        label={t(TEMPLATES_ZONE_TYPE)}
        variant="filled"
        fullWidth
        disabled={disabled}
        rules={optionalRules}
        actionsChange={(value) => onChangeField({ key: "type", value })}
        options={typeOptions}
      />
      {sourceOptions.length ? (
        <Select
          control={control}
          errors={errors}
          name="sourceKind"
          label={t(TEMPLATES_ZONE_SOURCE)}
          variant="filled"
          fullWidth
          disabled={disabled}
          rules={optionalRules}
          actionsChange={(value) => onChangeField({ key: "sourceKind", value })}
          options={sourceOptions}
        />
      ) : null}
      {sourceKind === "fixed" ? (
        <TextField
          control={control}
          errors={errors}
          name="fixedValue"
          label={t(TEMPLATES_FIXED_VALUE)}
          variant="filled"
          fullWidth
          disabled={disabled}
          rules={optionalRules}
          actionsChange={(value) => onChangeField({ key: "fixedValue", value })}
        />
      ) : null}
      {sourceKind === "data" ? (
        <TextField
          control={control}
          errors={errors}
          name="variable"
          label={t(TEMPLATES_VARIABLE)}
          variant="filled"
          fullWidth
          disabled={disabled}
          rules={optionalRules}
          actionsChange={(value) => onChangeField({ key: "variable", value })}
        />
      ) : null}
      {sourceKind === "computed" ? (
        <TextField
          control={control}
          errors={errors}
          name="expression"
          label={t(TEMPLATES_EXPRESSION)}
          variant="filled"
          fullWidth
          multiline
          minRows={2}
          disabled={disabled}
          rules={optionalRules}
          actionsChange={(value) => onChangeField({ key: "expression", value })}
        />
      ) : null}
      {sourceKind === "generated" ? (
        <TextField
          control={control}
          errors={errors}
          name="promptTemplate"
          label={t(TEMPLATES_PROMPT)}
          variant="filled"
          fullWidth
          multiline
          minRows={3}
          disabled={disabled}
          rules={optionalRules}
          actionsChange={(value) =>
            onChangeField({ key: "promptTemplate", value })
          }
        />
      ) : null}
      {showBackgroundCheckbox ? (
        <FormControlLabel
          sx={{ mx: 0, alignItems: "center" }}
          control={
            <Checkbox
              checked={isCanvasBackground}
              disabled={disabled}
              onChange={(event) =>
                onChangeField({
                  key: "isBackground",
                  value: event.target.checked,
                })
              }
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, fontSize: "13px" }}
            >
              {t(TEMPLATES_IS_BACKGROUND)}
            </Typography>
          }
        />
      ) : null}
      {sourceKind === "asset" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "text.secondary", fontSize: "13px" }}
          >
            {t(TEMPLATES_ASSET_REF)}
          </Typography>
          {assetUrl ? (
            <Box
              sx={{
                width: "100%",
                aspectRatio: "1 / 1",
                borderRadius: 2,
                border: "1px solid #E4E4E7",
                bgcolor: "action.hover",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={assetUrl}
                alt=""
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          ) : null}
          <input
            ref={assetFileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file || !onUploadAsset) return;
              onUploadAsset({ file });
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              color="secondary"
              disabled={disabled}
              loading={isUploadingAsset}
              onClick={() => assetFileInputRef.current?.click()}
            >
              {t(assetUrl ? TEMPLATES_CHANGE_ASSET : TEMPLATES_SELECT_ASSET)}
            </Button>
          </Box>
        </Box>
      ) : null}
      {type === "text" ? (
        <>
          <FontFamilySelect
            control={control}
            errors={errors}
            disabled={disabled}
            onChangeField={onChangeField}
          />
          <ColorField
            control={control}
            errors={errors}
            name="color"
            label={t(TEMPLATES_FONT_COLOR)}
            fullWidth
            disabled={disabled}
            rules={optionalRules}
            actionsChange={(value) => onChangeField({ key: "color", value })}
          />
          <Select
            control={control}
            errors={errors}
            name="align"
            label={t(TEMPLATES_ALIGN)}
            variant="filled"
            fullWidth
            disabled={disabled}
            rules={optionalRules}
            actionsChange={(value) => onChangeField({ key: "align", value })}
            options={[
              { value: "left", label: t(TEMPLATES_ALIGN_LEFT) },
              { value: "center", label: t(TEMPLATES_ALIGN_CENTER) },
              { value: "right", label: t(TEMPLATES_ALIGN_RIGHT) },
            ]}
          />
          <Select
            control={control}
            errors={errors}
            name="orientation"
            label={t(TEMPLATES_ORIENTATION)}
            variant="filled"
            fullWidth
            disabled={disabled}
            rules={optionalRules}
            actionsChange={(value) =>
              onChangeField({ key: "orientation", value })
            }
            options={[
              {
                value: "horizontal",
                label: t(TEMPLATES_ORIENTATION_HORIZONTAL),
              },
              {
                value: "vertical",
                label: t(TEMPLATES_ORIENTATION_VERTICAL),
              },
            ]}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              control={control}
              errors={errors}
              name="minimumLines"
              label={t(TEMPLATES_MIN_LINES)}
              variant="filled"
              type="number"
              fullWidth
              disabled={disabled}
              rules={optionalRules}
              InputLabelProps={{ shrink: true }}
              actionsChange={(value) =>
                onChangeField({
                  key: "minimumLines",
                  value: value === "" ? undefined : Number(value),
                })
              }
            />
            <TextField
              control={control}
              errors={errors}
              name="maximumLines"
              label={t(TEMPLATES_MAX_LINES)}
              variant="filled"
              type="number"
              fullWidth
              disabled={disabled}
              rules={optionalRules}
              InputLabelProps={{ shrink: true }}
              actionsChange={(value) =>
                onChangeField({
                  key: "maximumLines",
                  value: value === "" ? undefined : Number(value),
                })
              }
            />
          </Box>
        </>
      ) : null}
      {type === "group" ? (
        <>
          <TextField
            control={control}
            errors={errors}
            name="frameWidth"
            label={t(TEMPLATES_FRAME_W)}
            variant="filled"
            type="number"
            fullWidth
            disabled={disabled}
            rules={optionalRules}
            actionsChange={(value) =>
              onChangeField({
                key: "frameWidth",
                value: value === "" ? undefined : Number(value),
              })
            }
          />
          <TextField
            control={control}
            errors={errors}
            name="frameHeight"
            label={t(TEMPLATES_FRAME_H)}
            variant="filled"
            type="number"
            fullWidth
            disabled={disabled}
            rules={optionalRules}
            actionsChange={(value) =>
              onChangeField({
                key: "frameHeight",
                value: value === "" ? undefined : Number(value),
              })
            }
          />
          <Select
            control={control}
            errors={errors}
            name="clipContent"
            label={t(TEMPLATES_CLIP)}
            variant="filled"
            fullWidth
            disabled={disabled}
            rules={optionalRules}
            actionsChange={(value) =>
              onChangeField({ key: "clipContent", value: value === "true" })
            }
            options={[
              { value: "true", label: t(YES) },
              { value: "false", label: t(NO) },
            ]}
          />
        </>
      ) : null}
      <FieldGroup title={t(TEMPLATES_POSITION)}>
        <TextField
          control={control}
          errors={errors}
          name="posX"
          label={t(TEMPLATES_POS_X)}
          variant="filled"
          type="number"
          fullWidth
          disabled={geometryDisabled}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeField({
              key: "posX",
              value: value === "" ? undefined : Number(value),
            })
          }
        />
        <TextField
          control={control}
          errors={errors}
          name="posY"
          label={t(TEMPLATES_POS_Y)}
          variant="filled"
          type="number"
          fullWidth
          disabled={geometryDisabled}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeField({
              key: "posY",
              value: value === "" ? undefined : Number(value),
            })
          }
        />
      </FieldGroup>
      <FieldGroup title={t(TEMPLATES_SIZE)}>
        <TextField
          control={control}
          errors={errors}
          name="posW"
          label={t(TEMPLATES_POS_W)}
          variant="filled"
          type="number"
          fullWidth
          disabled={geometryDisabled}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeField({
              key: "posW",
              value: value === "" ? undefined : Number(value),
            })
          }
        />
        <TextField
          control={control}
          errors={errors}
          name="posH"
          label={t(TEMPLATES_POS_H)}
          variant="filled"
          type="number"
          fullWidth
          disabled={geometryDisabled}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeField({
              key: "posH",
              value: value === "" ? undefined : Number(value),
            })
          }
        />
      </FieldGroup>
    </Box>
  );
};

export default LayerPropertiesPanel;
